import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  drawerStyle,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  displayFlexItem,
  getStatusFlow,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { OTPreApprovalHeader } from "pages/TeamZone/TeamZoneHeaders";
import { Typography, Box, Paper, Drawer } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CustomButton } from "components/CustomComponents";
import { showToast } from "services/utils/Status";
import { NewPreApproval } from "./NewPreApproval";
import { ViewPreApproval } from "./ViewPreApproval";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { StatusRow } from "components/CustomComponents/StatusRow";

export const OTPreApproval = () => {
  const { domain, userScreenAccess, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [otPreApprovalDetails, setOtPreApprovalDetails] = useState([]);
  const containerRef = useRef(null);
  const [preApprovalData, setPreApprovalData] = useState([]);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [handleLoader, setHandleLoader] = useState(false);
  const [getId, setGetId] = useState("");
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState("");
  const {
    result: OTPreApprovalData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}Team_Zone/Approve/Over_time/ot-pre-approve/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`
  );
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approve",
    "OT Pre-Approval"
  );

  const createOTPreApproval = () => {
    setIsCreatePanelOpen(true);
  };

  const handleViewPanel = () => {
    setIsViewPanelOpen(true);
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggle = (index, value) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
    setExpandedData(value);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (OTPreApprovalData?.length !== 0 && OTPreApprovalData && handleLoader) {
      const item = OTPreApprovalData.find((d) => d.id === getId);
      setPreApprovalData(item);
    }
  }, [getId, handleLoader, OTPreApprovalData]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOtPreApprovalDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  const NewPayrunButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          createOTPreApproval();
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create overtime pre approval"
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHandleLoader(false);
    }
  }, [reCallApi, topbarCompanyId]);

  return !handleLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Overtime Pre-Approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all your pending and history of overtime requests awaiting
            manager review or approval.
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title={"Overtime Pre-Approval Records"}
            filterHeaders={OTPreApprovalHeader}
            data={OTPreApprovalData}
            filteredData={otPreApprovalDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={NewPayrunButton}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 580 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {OTPreApprovalHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {otPreApprovalDetails?.length === 0 ? (
                    <InfoMessage dataLength={OTPreApprovalData} />
                  ) : (
                    otPreApprovalDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value, index) => (
                        <React.Fragment key={index}>
                          <Mui.TableRow sx={tableRowSize}>
                            {OTPreApprovalHeader.map((head) => (
                              <Mui.TableCell
                                key={head.key}
                                onClick={() => {
                                  if (check_access) {
                                    if (head.key === "expand") {
                                      handleToggle(index, value);
                                    } else {
                                      handleViewPanel();
                                      setPreApprovalData(value);
                                      setExpandedIndex(null);
                                    }
                                  } else {
                                    showToast(
                                      "error",
                                      "Access denied: You are not authorized to modify overtime pre approval"
                                    );
                                  }
                                }}
                                sx={{
                                  width:
                                    head.key === "expand" ? "20px" : "auto",
                                }}
                              >
                                {head.key === "expand" ? (
                                  <ArrowRightIcon
                                    sx={{
                                      transition: "0.3s",
                                      transform:
                                        expandedIndex === index
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                      cusror: "pointer",
                                    }}
                                  />
                                ) : head.key === "employee_name" ? (
                                  <Mui.Stack direction="Row">
                                    <Mui.Avatar
                                      sx={{ width: 23, height: 23 }}
                                      alt={value?.employee_name
                                        ?.charAt(0)
                                        .toUpperCase()}
                                      src={value?.image_url}
                                    />
                                    &nbsp;
                                    {value?.employee_name}
                                  </Mui.Stack>
                                ) : head.key === "status" ? (
                                  <Mui.Box sx={displayFlexItem}>
                                    <StatusBadge
                                      color={value?.color_code}
                                      label={value?.status || "N/A"}
                                    />
                                  </Mui.Box>
                                ) : (
                                  value[head.key] ?? "N/A"
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>

                          {expandedIndex === index && (
                            <Mui.TableRow>
                              <Mui.TableCell
                                colSpan={OTPreApprovalHeader.length}
                                sx={{ p: 2 }}
                              >
                                {getStatusFlow(expandedData)[
                                  expandedData?.status
                                ]?.map((item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ))}
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {otPreApprovalDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={otPreApprovalDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={() => {
          isCreatePanelOpen ? handleCreateClosePanel() : handleViewClosePanel();
        }}
        sx={drawerStyle}
      >
        {isCreatePanelOpen ? (
          <NewPreApproval
            onClose={handleCreateClosePanel}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
          />
        ) : isViewPanelOpen ? (
          <ViewPreApproval
            onClose={handleViewClosePanel}
            preApprovalData={preApprovalData}
            reCallApi={reCallApi}
            setGetId={setGetId}
            setHandleLoader={setHandleLoader}
            screenAccess={check_access}
          />
        ) : null}
      </Drawer>
    </React.Fragment>
  );
};
